import { Query } from '../api';
import {
  Canvas,
  Page,
  Widget,
  CanvasListResponse,
  CanvasResponse,
  PageListResponse,
  PageResponse,
  WidgetListResponse,
  WidgetResponse,
  CreateCanvasRequest,
  UpdateCanvasRequest,
  CreatePageRequest,
  UpdatePageRequest,
  CreateWidgetRequest,
  UpdateWidgetRequest,
} from '../../types/canvas.types';

export class CanvasApi {
  // Canvas endpoints
  static listCanvases(accountId: string) {
    return new Query(`/accounts/${accountId}/canvases`);
  }

  static getCanvas(accountId: string, canvasId: string) {
    return new Query(`/accounts/${accountId}/canvases/${canvasId}`);
  }

  static createCanvas(accountId: string, data: CreateCanvasRequest) {
    const requestData = {
      type: 'canvas',
      attributes: data,
    };
    console.log(
      '🚀 Canvas creation request data:',
      JSON.stringify(requestData, null, 2),
    );
    return new Query(`/accounts/${accountId}/canvases`, null, {
      httpMethod: 'post',
    }).setData(requestData, null);
  }

  static updateCanvas(
    accountId: string,
    canvasId: string,
    data: UpdateCanvasRequest,
  ) {
    return new Query(`/accounts/${accountId}/canvases/${canvasId}`, null, {
      httpMethod: 'patch',
    }).setData(
      {
        type: 'canvas',
        id: canvasId,
        attributes: data,
      },
      null,
    );
  }

  static deleteCanvas(accountId: string, canvasId: string) {
    return new Query(`/accounts/${accountId}/canvases/${canvasId}`, null, {
      httpMethod: 'delete',
    });
  }

  // Page endpoints
  static listPages(accountId: string, canvasId: string) {
    return new Query(`/accounts/${accountId}/canvases/${canvasId}/pages`);
  }

  static getPage(accountId: string, pageId: string) {
    return new Query(`/accounts/${accountId}/pages/${pageId}`);
  }

  static createPage(
    accountId: string,
    canvasId: string,
    data: CreatePageRequest,
  ) {
    return new Query(
      `/accounts/${accountId}/canvases/${canvasId}/pages`,
      null,
      { httpMethod: 'post' },
    ).setData(
      {
        type: 'page',
        attributes: data,
      },
      null,
    );
  }

  static updatePage(
    accountId: string,
    pageId: string,
    data: UpdatePageRequest,
  ) {
    return new Query(`/accounts/${accountId}/pages/${pageId}`, null, {
      httpMethod: 'patch',
    }).setData(
      {
        type: 'page',
        id: pageId,
        attributes: data,
      },
      pageId,
    );
  }

  static deletePage(accountId: string, pageId: string) {
    return new Query(`/accounts/${accountId}/pages/${pageId}`, null, {
      httpMethod: 'delete',
    });
  }

  // Widget endpoints
  static listWidgets(accountId: string, pageId: string) {
    return new Query(`/accounts/${accountId}/pages/${pageId}/widgets`);
  }

  static getWidget(accountId: string, widgetId: string) {
    return new Query(`/accounts/${accountId}/widgets/${widgetId}`);
  }

  static createWidget(
    accountId: string,
    pageId: string,
    data: CreateWidgetRequest,
  ) {
    return new Query(`/accounts/${accountId}/pages/${pageId}/widgets`, null, {
      httpMethod: 'post',
    }).setData(
      {
        type: 'widget',
        attributes: data,
      },
      null,
    );
  }

  static updateWidget(
    accountId: string,
    widgetId: string,
    data: UpdateWidgetRequest,
  ) {
    return new Query(`/accounts/${accountId}/widgets/${widgetId}`, null, {
      httpMethod: 'patch',
    }).setData(
      {
        type: 'widget',
        id: widgetId,
        attributes: data,
      },
      null,
    );
  }

  static deleteWidget(accountId: string, widgetId: string) {
    return new Query(`/accounts/${accountId}/widgets/${widgetId}`, null, {
      httpMethod: 'delete',
    });
  }

  static duplicateWidget(accountId: string, widgetId: string) {
    return new Query(
      `/accounts/${accountId}/widgets/${widgetId}/duplicate`,
      null,
      { httpMethod: 'post' },
    );
  }

  // Slug validation endpoints - these return plain JSON, not JSON:API format
  static async validateCanvasSlug(accountId: string, slug: string) {
    const query = new Query(
      `/accounts/${accountId}/canvases/validate-slug?slug=${encodeURIComponent(slug)}`,
    );
    // Get the raw axios response instead of going through the Model wrapper
    const response = await query.httpClient.request({
      url: query.getURL(),
      method: 'get',
      withCredentials: true,
    });
    return response.data; // Return the plain JSON directly
  }

  static async validatePageSlug(
    accountId: string,
    canvasId: string,
    slug: string,
  ) {
    const query = new Query(
      `/accounts/${accountId}/canvases/${canvasId}/pages/validate-slug?slug=${encodeURIComponent(slug)}`,
    );
    // Get the raw axios response instead of going through the Model wrapper
    const response = await query.httpClient.request({
      url: query.getURL(),
      method: 'get',
      withCredentials: true,
    });
    return response.data; // Return the plain JSON directly
  }
}

// Helper functions for extracting data from API responses
export const extractCanvasData = (response: any): Canvas => {
  const data = response.data || response;

  // Handle both JSON:API format and flattened Model format
  if (data.attributes) {
    // JSON:API format
    return {
      id: data.id,
      name: data.attributes.name,
      slug: data.attributes.slug,
      settings: data.attributes.settings || {},
      datastore_id: data.attributes.datastore_id,
      created_at: data.attributes.created_at,
      updated_at: data.attributes.updated_at,
      created_by_id: data.attributes.created_by_id,
      updated_by_id: data.attributes.updated_by_id,
    };
  } else {
    // Flattened Model format (what we're actually getting)
    return {
      id: data.id,
      name: data.name,
      slug: data.slug,
      settings: data.settings || {},
      datastore_id: data.datastore_id,
      created_at: data.created_at,
      updated_at: data.updated_at,
      created_by_id: data.created_by_id,
      updated_by_id: data.updated_by_id,
    };
  }
};

export const extractCanvasListData = (response: any): Canvas[] => {
  const data = response.data || response;
  if (Array.isArray(data)) {
    return data.map(extractCanvasData);
  }
  return [];
};

export const extractPageData = (response: any): Page => {
  const data = response.data || response;

  // Handle both JSON:API format and flattened Model format
  if (data.attributes) {
    // JSON:API format
    return {
      id: data.id,
      canvas_id: data.attributes.canvas_id,
      title: data.attributes.title,
      slug: data.attributes.slug,
      layout_type: data.attributes.layout_type,
      order: data.attributes.order,
      settings: data.attributes.settings || {},
      datastore_id: data.attributes.datastore_id,
      created_at: data.attributes.created_at,
      updated_at: data.attributes.updated_at,
      created_by_id: data.attributes.created_by_id,
      updated_by_id: data.attributes.updated_by_id,
    };
  } else {
    // Flattened Model format
    return {
      id: data.id,
      canvas_id: data.canvas_id,
      title: data.title,
      slug: data.slug,
      layout_type: data.layout_type,
      order: data.order,
      settings: data.settings || {},
      datastore_id: data.datastore_id,
      created_at: data.created_at,
      updated_at: data.updated_at,
      created_by_id: data.created_by_id,
      updated_by_id: data.updated_by_id,
    };
  }
};

export const extractPageListData = (response: any): Page[] => {
  const data = response.data || response;
  if (Array.isArray(data)) {
    return data.map(extractPageData);
  }
  return [];
};

export const extractWidgetData = (response: any): Widget => {
  const data = response.data || response;

  // Handle both JSON:API format and flattened Model format
  if (data.attributes) {
    // JSON:API format
    return {
      id: data.id,
      page_id: data.attributes.page_id,
      widget_type: data.attributes.widget_type,
      position: data.attributes.position || {},
      configuration: data.attributes.configuration || {},
      data_source: data.attributes.data_source || {},
      order: data.attributes.order,
      created_at: data.attributes.created_at,
      updated_at: data.attributes.updated_at,
      created_by_id: data.attributes.created_by_id,
      updated_by_id: data.attributes.updated_by_id,
    };
  } else {
    // Flattened Model format
    return {
      id: data.id,
      page_id: data.page_id,
      widget_type: data.widget_type,
      position: data.position || {},
      configuration: data.configuration || {},
      data_source: data.data_source || {},
      order: data.order,
      created_at: data.created_at,
      updated_at: data.updated_at,
      created_by_id: data.created_by_id,
      updated_by_id: data.updated_by_id,
    };
  }
};

export const extractWidgetListData = (response: any): Widget[] => {
  const data = response.data || response;
  if (Array.isArray(data)) {
    return data.map(extractWidgetData);
  }
  return [];
};
